import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List, Map } from 'immutable';
import { merger } from '../../../../utils/helpers/model.helper';
import {
  IEditTaxRateProperties,
  MAX_TERMS,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { States } from '../../../document-data/sub-models/states.model';
import { TaxTotal } from '../../../document-data/sub-models/tax-total.model';
import { CONTENT_KEY } from '../../../entity-data/entity.model';
import { IDatePickerFieldOptions } from '../../../form-master/components/date-picker-field/date-picker-field.component';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { IFormMasterBuilder } from '../../../form-master/form-master.interface';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { termsLocalized } from '../../../shared/constants/terms.constant';
import { DateTimeService } from 'i2g-ng-utils';

@Injectable()
export class AsideCreateBuilder implements IFormMasterBuilder {
  FORM_KEYS = {
    DATE: 'date',
    TERMS: 'terms',
    DUE_DATE: 'dueDate',
    DOCUMENT_NUMBER: 'documentNumber',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _dateTimeService: DateTimeService,
    private _translateService: TranslateService
  ) {}

  buildForm(docDate: string, docTerms: number, docNumber: string): FormGroup {
    const formKeys = this.FORM_KEYS;

    return this._formBuilder.group({
      [formKeys.DOCUMENT_NUMBER]: [
        docNumber,
        [Validators.required, Validators.maxLength(19)],
      ],
      [formKeys.DATE]: [docDate],
      [formKeys.TERMS]: [docTerms, Validators.max(MAX_TERMS)],
      [formKeys.DUE_DATE]: [this.buildDueDate(docDate, docTerms)],
    });
  }

  buildTermsOptions(terms: number): List<ISelectOption> {
    return termsLocalized(this._translateService, false, terms);
  }

  buildTaxSummary(
    taxInfo: List<Tax>,
    taxTotals: List<TaxTotal>
  ): List<{ info: Tax; taxTotals: List<TaxTotal> }> {
    return taxTotals
      .groupBy((taxTotal: TaxTotal) => taxTotal.taxId)
      .map((taxes, taxId) => {
        return {
          info: taxInfo.find((tax: Tax) => tax.id === taxId),
          taxTotals: taxes.sortBy((tax) => tax.rate) as List<TaxTotal>,
        };
      })
      .toList();
  }

  buildDueDate(docDate: string, daysAmount: number) {
    return this._dateTimeService.addTime(docDate, daysAmount, 'days');
  }

  buildDatePickerOptions(minDate: string): IDatePickerFieldOptions {
    return {
      minDate,
      maxDate: this._dateTimeService.addTime(minDate, MAX_TERMS, 'days'),
    };
  }

  buildDocumentPropsFromDocNumber(docNumber: string): Map<string, any> {
    return fromJS({
      [CONTENT_KEY]: {
        [Document.CONTENT_KEYS.DOC_NUMBER]: docNumber,
      },
    });
  }

  buildDocumentPropsFromDateTerms(
    docDate: string,
    terms: number,
    showDueDate: boolean
  ): Map<string, any> {
    let result: any;

    if (showDueDate) {
      result = {
        [Document.KEYS.STATES]: {
          [States.KEYS.DUE_DATE]: this.buildDueDate(docDate, terms || 0),
        },
        [CONTENT_KEY]: {
          [Document.CONTENT_KEYS.DOC_DATE]: docDate,
          [Document.CONTENT_KEYS.SETTINGS]: {
            [DocumentPresetSettings.KEYS.TERMS]: terms,
          },
        },
      };
    } else {
      result = {
        [CONTENT_KEY]: {
          [Document.CONTENT_KEYS.DOC_DATE]: docDate,
        },
      };
    }

    return fromJS(result);
  }

  buildDocumentPropsFromDiscount(
    discountProps: Map<string, any>
  ): Map<string, any> {
    return fromJS({
      [CONTENT_KEY]: {
        [Document.CONTENT_KEYS.DISCOUNT]: discountProps,
      },
    });
  }

  buildDocumentPropsFromTax(
    taxes: List<Tax>,
    tax: Tax
  ): IEditTaxRateProperties {
    const taxId = tax.id;
    const taxPosition = taxes.findIndex((t: Tax) => t.id === taxId);
    const newTaxes =
      taxPosition < 0
        ? taxes.push(tax)
        : taxes.set(taxPosition, merger(taxes.get(taxPosition), tax));
    const properties = fromJS({
      [CONTENT_KEY]: {
        [Document.CONTENT_KEYS.TAXES]: newTaxes,
      },
    });

    return {
      tax: tax,
      documentProperties: properties,
    };
  }

  buildDocumentPropsFromDeposit(deposit: Deposit) {
    return fromJS({
      [CONTENT_KEY]: {
        [Document.CONTENT_KEYS.DEPOSIT]: deposit,
      },
    });
  }
}
